/**
 * App modals (contracts apps.ts views, Slack's modals): the stack of views an app opened for me with a trigger_id
 * from a slash command or a button press, the values I've entered in the top one, and its errors.
 *
 * - **Showing:** stacks arrive in the command / button answer (`AppInteractionResponse.views`) and as the
 *   `app.views` realtime event. A stack opened from another of my clients (its `clientToken` isn't one this client
 *   made) isn't shown, like Slack, unless it's the stack already open here (updates and closes).
 * - **Values:** each view starts from `initialKitValues`; after an update (same view id, new hash) values of elements
 *   that are still there are kept, like Slack.
 * - **Submit:** required inputs and lengths are checked here first (`kitInputErrors`); then the app answers with a
 *   new stack, errors by block id (shown under the inputs) or nothing (the view closes).
 * - **Elements:** changes in an input block with `dispatch_action`, and every element outside input blocks (buttons,
 *   overflow menus, selects in sections and actions) send `block_actions` to the app at once.
 *
 * One per workspace (WorkspaceViewModel.modal). Clients without the views API never get a stack.
 */
import type { KitStateValues, ViewStack } from "@bundle/contracts";
import { type KitButtonNode, type KitInputError, type KitNode, type KitOverflowNode, type KitValueNode, type KitValues, initialKitValues, kitInputErrors, kitState, kitStateValue, kitValueElements, renderKit } from "../logic/blockKit.ts";
import { type MessageDescriptor, msg } from "../i18n.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

export type AppModalState = {
  /** The open modal (the last view shows); absent = none. */
  stack?: ViewStack;
  /** The top view drawn (renderKit of its blocks). */
  nodes: KitNode[];
  /** What I've entered or picked in the top view. */
  values: KitValues;
  /** Under the inputs, by block id: my mistakes (required, length) or the app's `response_action: "errors"`. */
  errors: Record<string, MessageDescriptor>;
  /** Waiting for the app to answer the submit. */
  submitting: boolean;
  /** Above the buttons: the app couldn't be reached ("We had some trouble connecting…"). */
  notice?: MessageDescriptor;
};

/** A client-side input error → what shows under the input. */
export function inputErrorMessage(error: KitInputError, element: KitValueNode): MessageDescriptor {
  switch (error) {
    case "required":
      return msg("appModal.error.required");
    case "tooShort":
      return msg("appModal.error.tooShort", { count: element.kind === "text_input" ? (element.minLength ?? 0) : 0 });
    case "tooLong":
      return msg("appModal.error.tooLong", { count: element.kind === "text_input" ? (element.maxLength ?? 0) : 0 });
    case "tooMany":
      return msg("appModal.error.tooMany", { count: element.kind === "select" ? (element.maxSelected ?? 0) : 0 });
  }
}

export class AppModalViewModel extends ViewModel<AppModalState> {
  private readonly ws: WorkspaceViewModel;
  /** clientTokens this client sent with commands and button presses. */
  private readonly tokens = new Set<string>();
  /** What I entered in views that aren't on top (by view id). */
  private readonly saved = new Map<string, KitValues>();
  private seq = 0;

  constructor(ws: WorkspaceViewModel) {
    super({ nodes: [], values: {}, errors: {}, submitting: false });
    this.ws = ws;
  }

  /** A new id for a command / button press, so the modal it opens shows on this client. */
  newClientToken(): string {
    this.seq += 1;
    const token = `${this.ws.deps.clock.now().toString(36)}-${this.seq}-${Math.floor(Math.random() * 1e9).toString(36)}`;
    this.tokens.add(token);
    return token;
  }

  /** The top view (absent = no modal). */
  get top() {
    return this.state.stack?.views.at(-1);
  }

  /**
   * A stack from the server (an answer or the `app.views` event). `mine`: it came back from my own request (always
   * shown); otherwise it shows when it was opened from this client or replaces the stack open here.
   */
  apply(stack: ViewStack, mine = false): void {
    const current = this.state.stack;
    const ours = mine || (stack.clientToken !== undefined && this.tokens.has(stack.clientToken)) || current?.rootId === stack.rootId;
    if (!ours) return;
    const top = stack.views.at(-1);
    if (top === undefined) {
      this.saved.clear();
      this.setState({ nodes: [], values: {}, errors: {}, submitting: false });
      return;
    }
    const before = current?.views.at(-1);
    if (before !== undefined && before.id === top.id && before.hash === top.hash) {
      this.update({ stack });
      return;
    }
    const nodes = renderKit(top.view.blocks);
    const fresh = initialKitValues(nodes, top.channelId);
    // Views under the top keep what I entered, for when I come back to them (Slack).
    if (before !== undefined) this.saved.set(before.id, this.state.values);
    const live = new Set(stack.views.map((v) => v.id));
    for (const id of this.saved.keys()) if (!live.has(id)) this.saved.delete(id);
    // An update of the same view, or coming back to one, keeps what I entered in elements that are still there.
    const kept = this.saved.get(top.id);
    if (kept !== undefined) {
      for (const { blockId, element } of kitValueElements(nodes)) {
        const v = kept[blockId]?.[element.actionId];
        if (v !== undefined) (fresh[blockId] ??= {})[element.actionId] = v;
      }
    }
    this.saved.delete(top.id);
    const next: AppModalState = { stack, nodes, values: fresh, errors: before?.id === top.id ? this.state.errors : {}, submitting: false };
    this.setState(next);
  }

  /** My open modal from the server (after starting or reconnecting); online only. */
  load(): void {
    const get = this.ws.deps.api.currentViews;
    if (get === undefined) return;
    get.call(this.ws.deps.api, this.ws.wsId).then(
      (stack) => {
        if (stack !== undefined) this.apply(stack, true);
        else if (this.state.stack !== undefined) this.apply({ rootId: this.state.stack.rootId, views: [] }, true);
      },
      () => undefined,
    );
  }

  private find(blockId: string, actionId: string): { element: KitValueNode; input: boolean; dispatch: boolean } | undefined {
    for (const n of this.state.nodes) {
      if (n.kind === "input" && n.blockId === blockId && n.element.actionId === actionId) return { element: n.element, input: true, dispatch: n.dispatch };
    }
    const found = kitValueElements(this.state.nodes).find((x) => x.blockId === blockId && x.element.actionId === actionId);
    return found === undefined ? undefined : { element: found.element, input: found.input, dispatch: false };
  }

  /** An element's new value (text typed, option picked, day chosen…). Elements outside inputs, and dispatching inputs, tell the app. */
  setValue(blockId: string, actionId: string, value: string[]): void {
    const found = this.find(blockId, actionId);
    if (found === undefined) return;
    const values: KitValues = { ...this.state.values, [blockId]: { ...this.state.values[blockId], [actionId]: value } };
    const { [blockId]: _cleared, ...errors } = this.state.errors;
    this.update({ values, errors });
    // Text inputs dispatch on Enter only (Slack's default trigger); the platform calls `dispatchText`.
    if ((!found.input || found.dispatch) && found.element.kind !== "text_input") this.sendAction(blockId, actionId, undefined, kitStateValue(found.element, value, this.ws.kitLabels[blockId]?.[actionId]));
  }

  /** Enter in a text input of an input block with `dispatch_action`. */
  dispatchText(blockId: string, actionId: string): void {
    const found = this.find(blockId, actionId);
    if (found === undefined || !found.dispatch) return;
    this.sendAction(blockId, actionId, undefined, kitStateValue(found.element, this.state.values[blockId]?.[actionId] ?? [], this.ws.kitLabels[blockId]?.[actionId]));
  }

  /** A button in the modal (after its confirm dialog, if any). */
  press(blockId: string, button: KitButtonNode, openUrl?: (url: string) => void): void {
    if (button.url !== undefined) openUrl?.(button.url);
    this.sendAction(blockId, button.actionId, button.value, undefined);
  }

  /** A choice from an overflow menu in the modal. */
  pickOverflow(blockId: string, menu: KitOverflowNode, value: string): void {
    const o = menu.options.find((x) => x.value === value);
    if (o === undefined) return;
    this.sendAction(blockId, menu.actionId, undefined, { type: "overflow", selected_option: { text: { type: "plain_text", text: o.label }, value } });
  }

  private sendAction(blockId: string, actionId: string, value: string | undefined, selected: ReturnType<typeof kitStateValue> | undefined): void {
    const top = this.top;
    const act = this.ws.deps.api.viewAction;
    if (top === undefined || act === undefined) return;
    const req: { actionId: string; blockId: string; values: KitStateValues; value?: string; selected?: ReturnType<typeof kitStateValue> } = { actionId, blockId, values: kitState(this.state.nodes, this.state.values, this.ws.kitLabels) };
    if (value !== undefined) req.value = value;
    if (selected !== undefined) req.selected = selected;
    act.call(this.ws.deps.api, this.ws.wsId, top.id, req).then(
      () => undefined,
      () => this.update({ notice: msg("composer.appAction.failed") }),
    );
  }

  /** The submit button: checks the inputs, then sends view_submission and shows what the app answered. */
  submit(): void {
    const top = this.top;
    const send = this.ws.deps.api.submitView;
    if (top === undefined || send === undefined || this.state.submitting) return;
    const problems = kitInputErrors(this.state.nodes, this.state.values);
    const ids = Object.keys(problems);
    if (ids.length > 0) {
      const errors: Record<string, MessageDescriptor> = {};
      for (const n of this.state.nodes) if (n.kind === "input" && problems[n.blockId] !== undefined) errors[n.blockId] = inputErrorMessage(problems[n.blockId]!, n.element);
      this.update({ errors });
      return;
    }
    const { notice: _n, ...rest } = this.state;
    this.setState({ ...rest, submitting: true, errors: {} });
    send.call(this.ws.deps.api, this.ws.wsId, top.id, { values: kitState(this.state.nodes, this.state.values, this.ws.kitLabels) }).then(
      (out) => {
        if (this.isDisposed) return;
        this.update({ submitting: false });
        if (out.error !== undefined) {
          this.update({ notice: msg("appModal.unreachable") });
          return;
        }
        if (out.errors !== undefined && Object.keys(out.errors).length > 0) {
          const errors: Record<string, MessageDescriptor> = {};
          for (const [blockId, text] of Object.entries(out.errors)) errors[blockId] = msg("appModal.appError", { text });
          this.update({ errors });
          return;
        }
        this.apply(out.views, true);
      },
      () => {
        if (!this.isDisposed) this.update({ submitting: false, notice: msg("appModal.unreachable") });
      },
    );
  }

  /** Cancel / back (`all` false: the top view) or X / Escape (`all`: the whole modal). Closes here at once. */
  close(all = false): void {
    const stack = this.state.stack;
    const top = this.top;
    if (stack === undefined || top === undefined) return;
    const clear = all || top.view.clear_on_close === true;
    const views = clear ? [] : stack.views.slice(0, -1);
    const next: ViewStack = { rootId: stack.rootId, views };
    if (stack.clientToken !== undefined) next.clientToken = stack.clientToken;
    this.apply(next, true);
    const close = this.ws.deps.api.closeView;
    if (close === undefined) return;
    close.call(this.ws.deps.api, this.ws.wsId, top.id, clear ? { all: true } : {}).then(
      () => undefined,
      () => undefined,
    );
  }

  /** Dismisses the "couldn't reach the app" notice. */
  clearNotice(): void {
    if (this.state.notice === undefined) return;
    const { notice: _n, ...rest } = this.state;
    this.setState(rest);
  }
}
